import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { replace } from 'connected-react-router'
import { useLocation } from 'react-router'

import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import { getCruiseSearchStateFromParams, getCruiseOfferFiltersFromParams } from 'lib/cruises/cruiseUtils'

import { useGlobalCruisesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import useCruiseSearchFlashOffers from 'hooks/Cruise/useCruiseSearchFlashOffers'
import useCruiseSearchCount from 'hooks/Cruise/useCruiseSearchCount'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'

import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'

import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import type { OfferListAndAction, OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList, { OfferListAction } from 'components/OfferList/OfferList'
import BodyText from 'LuxKit/Typography/BodyText'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'

import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'

import CruiseSearchFiltersBar from './Filters/CruiseSearchFiltersBar'
import CruiseSearchFiltersSidebar from './Filters/CruiseSearchExtraFiltersSidebar'
import CruiseSearchFiltersMobile from './Filters/CruiseSearchFiltersMobile'
import CruiseSortDropdown from './Filters/Inputs/CruiseSortDropdown'
import CruiseHeadData from '../Common/CruiseHeadData'
import CruisesContact from '../Common/CruisesContact'
import { getListName } from 'analytics/enhanced-ecommerce'
import clsx from 'clsx'
import getOfferListKey from 'lib/offer/offerListKey'
import CruiseHeaderFilterChip from 'components/Cruises/SearchPage/Filters/CruiseHeaderFilterChip'
import CruiseOfferListNoResults from 'components/Cruises/Common/CruiseOfferListNoResults'
import CruiseSelectCabinType from './Filters/CruiseSelectCabinType'
import Divider from 'LuxKit/Divider'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import SiteTakeoverSearchBanner from 'takeovers/utils/components/SiteTakeoverSearchBanner'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'

import * as Analytics from 'analytics/analytics'

const PageContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  padding: ${rem(20)} 0;

  ${mediaQueryUp.desktop} {
    padding: ${rem(16)} 0;
    gap: ${rem(32)};
    grid-template-columns: ${rem(250)} 1fr;
    &.no-filters {
      grid-template-columns: 1fr;
    }
  }
`

const CruiseSearchPanel = styled.div`
  position: relative;
  padding: ${rem(20)} 0;
`

const StyledLayoutContainer = styled(LayoutContainer)`
  padding-inline: 0;

  ${mediaQueryUp.tablet} {
    padding-inline: ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    && {
      max-width: ${rem(1352)};
    }
  }
`

const CountWrapper = styled(Group)`
  padding-left: ${rem(16)};

  ${mediaQueryUp.tablet} {
    padding-left: 0;
  }
`

interface Props {
  shouldHideCruiseShipsFilters?: boolean
  initFilter?: App.OfferListFilters
  disableHeadData?: boolean
}

function CruisesSearchPage({ shouldHideCruiseShipsFilters = false, initFilter, disableHeadData = false }: Props) {
  const intl = useIntl()
  const queryParams = useQueryParams()
  const dispatch = useAppDispatch()
  const { search } = useLocation()

  const initialState = useMemo(() => ({
    ...getCruiseSearchStateFromParams(queryParams),
    ...(initFilter ? { filters: initFilter } : {}),
  }), [initFilter, queryParams])

  const { globalCruisesSearchDispatch, globalCruisesSearchState } = useGlobalCruisesSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    ...initialState,
  })

  const [filters, filtersSelected] = useMemo(() => {
    const [baseFilters, hasFilters] = getCruiseOfferFiltersFromParams(queryParams)
    if (initFilter) {
      return [{ ...baseFilters, ...initFilter }, true]
    }
    return [baseFilters, hasFilters]
  }, [initFilter, queryParams])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'CRUISE', 'Search Page Offer List'),
    { key: getOfferListKey(filters) },
  )
  const flashOffers = useCruiseSearchFlashOffers({ filters, globalFilters: globalCruisesSearchState })
  const [searchCount, fetchingSearchCount] = useCruiseSearchCount(filters)

  const interstitials = useMemo((): Array<OfferListV2Item> => ([{
    id: 'heading',
    position: 'start',
    show: 'hasOffers',
  }, {
    id: 'empty',
    position: 'start',
    show: 'hasNoOffers',
    element: !flashOffers.offerCount && <CruiseOfferListNoResults />,
  }]), [flashOffers.offerCount])

  const onSortChange = useCallback((nextSort: App.OfferListSortByOptions) => {
    const nextSearch = setSearchParamValue(search, 'sortBy', nextSort)
    dispatch(replace({ search: nextSearch }))
  }, [dispatch, search])

  const overrideFlashOfferList = useMemo<OfferListAndAction>(() => ({
    action: OfferListAction.OVERRIDE,
    offerList: flashOffers,
  }), [flashOffers])

  const count = searchCount + (flashOffers.offerCount ?? 0)

  useEffect(() => {
    if (!fetchingSearchCount && filtersSelected) {
      Analytics.trackClientEvent({
        subject: `Cruise Search Results Count - ${searchCount}`,
        action: 'search',
        category: 'cruise-search',
        type: 'interaction',
      })
    }
  }, [fetchingSearchCount, filtersSelected, searchCount])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch])

  const shouldShowBanner = useShouldShowBanner({ location: 'search', vertical: 'cruise' })

  const searchingForCruisesLabel = intl.formatMessage({
    id: 'oD0lOh16DMV38RNCyOqL',
    defaultMessage: 'Searching for cruises',
    description: 'Loading text shown while cruise search results are being fetched',
  })

  const cruisesFoundLabel = intl.formatMessage(
    {
      id: '3rQby87UONPzRyZdea3-',
      defaultMessage: '{count, plural, one {# cruise} other {# cruises}} found',
      description: 'Number of cruise search results found in the cruise offer or booking section',
    },
    { count },
  )

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
      <div>
        {!disableHeadData && <CruiseHeadData />}
        {shouldShowBanner && <SiteTakeoverSearchBanner vertical="cruise" />}

        <GlobalSearchContextProvider
          state={globalCruisesSearchState}
          dispatch={globalCruisesSearchDispatch}
        >
          <CSSBreakpoint as={Pane} min="tablet">
            <LayoutContainer>
              <CruiseSearchPanel>
                <CruiseSearchFiltersBar />
              </CruiseSearchPanel>
            </LayoutContainer>
          </CSSBreakpoint>

          <CSSBreakpoint min="desktop">
            <Divider kind="primary" />
          </CSSBreakpoint>

          {(fetchingSearchCount || !!searchCount || !!flashOffers.offerCount || filtersSelected) &&
            <CruiseSearchFiltersMobile filters={filters} onSortChange={handleSortChange} />}

          <CSSBreakpoint only="tablet" as={Pane}>
            <Group direction="vertical" gap={16} verticalAlign="center">
              <StyledLayoutContainer>
                <CruiseHeaderFilterChip filters={filters} onSortChange={handleSortChange} />
              </StyledLayoutContainer>
              <Divider kind="primary" />
              <StyledLayoutContainer>
                <CruiseSelectCabinType filters={filters} />
              </StyledLayoutContainer>
            </Group>
          </CSSBreakpoint>

          <StyledLayoutContainer>
            <PageContent className={clsx({ 'no-filters': !fetchingSearchCount && !searchCount && !flashOffers.offerCount && !filtersSelected })}>
              {(fetchingSearchCount || !!searchCount || !!flashOffers.offerCount || filtersSelected) &&
                <CSSBreakpoint min="desktop">
                  <CruiseSearchFiltersSidebar filters={filters} shouldHideCruiseShips={shouldHideCruiseShipsFilters} />
                </CSSBreakpoint>}
              <Group direction="vertical" gap={16}>
                <CSSBreakpoint min="desktop">
                  <CruiseSelectCabinType filters={filters} />
                </CSSBreakpoint>
                {(fetchingSearchCount || !!searchCount || filtersSelected) && <CountWrapper
                  direction="horizontal"
                  gap={8}
                  horizontalAlign="space-between"
                  desktopVerticalAlign="center"
                  tabletVerticalAlign="end"
                >
                  <Group direction="horizontal" gap={16} verticalAlign="center">
                    <BodyText variant="large">
                      {fetchingSearchCount && searchingForCruisesLabel}
                      {!fetchingSearchCount && cruisesFoundLabel}
                    </BodyText>
                    {fetchingSearchCount && <LoadingAnimation size={8} />}
                  </Group>

                  <CSSBreakpoint min="desktop">
                    <CruiseSortDropdown sort={filters.sortBy ?? 'recommended'} onChange={onSortChange} />
                  </CSSBreakpoint>
                </CountWrapper>}
                <OfferListEventsProvider tracking={tracking}>
                  <Group direction="vertical" gap={16}>
                    <OfferList
                      extraOfferListAndAction={overrideFlashOfferList}
                      tileStyle="search"
                      pageSize={30}
                    />

                    <OfferList
                      filters={filters}
                      tileStyle="search"
                      interstitials={interstitials}
                    />
                  </Group>
                </OfferListEventsProvider>
              </Group>
            </PageContent>
          </StyledLayoutContainer>
          <CruisesContact />
        </GlobalSearchContextProvider>
      </div>
    </AnalyticsPageContext.Provider>
  )
}

export default CruisesSearchPage
